import { MathUtils, Quaternion as ThreeQuaternion, Vector3 } from 'three';
import type * as zod from 'zod';

import type { CartesianPose } from '@sb/geometry';
import {
  applyRelativeOffset,
  convertEulerPose,
  posesAreEqual,
  tooltipCoordinatesToBaseCoordinates,
  getPlaneOrientation,
  EulerAngleToQuaternion,
  rotateVectorByQuaternion,
} from '@sb/geometry';
import type { TCPOffsetOption } from '@sb/motion-planning';
import { ArmTarget, DEFAULT_TCP_OFFSET_OPTION } from '@sb/motion-planning';
import type { StepFailure } from '@sb/routine-runner';
import { FailureKind } from '@sb/routine-runner';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';

import Step from '../Step';

import Arguments from './Arguments';
import Variables from './Variables';

type Arguments = zod.infer<typeof Arguments>;

type Variables = zod.infer<typeof Variables>;

export default class WaypointStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public initializeVariableState(): void {
    const { completedCount = 0 } = this.variablesForInitialization;

    this.variables = {
      completedCount,
      currentActivity: 'none',
    };
  }

  /**
   * Placeholder method for the play logic.
   * Implement the actual logic here.
   */
  public async _play(): Promise<void> {
    // TODO: Implement the play logic
  }

  // Utility to recursively find a step by id in a tree of steps
  private findStepById(steps: any[], id: string): any | undefined {
    for (const step of steps) {
      if (step.id === id) return step;

      if (step.steps && Array.isArray(step.steps)) {
        const found = this.findStepById(step.steps, id);
        if (found) return found;
      }
    }

    return undefined;
  }

  // Helper to resolve positionListID as either a space item or a waypoint step
  private async resolvePositionListReference(
    positionListID: string,
    positionListIndex: number,
    previousTargetPose: CartesianPose,
  ): Promise<any> {
    if (positionListID === 'current-position') {
      return { pose: previousTargetPose };
    }

    if (
      positionListID === 'expression' &&
      this.args.target &&
      'expression' in this.args.target &&
      this.args.target.expression
    ) {
      const expressionTarget = await this.routineContext.evaluateExpression(
        this.args.target.expression,
      );

      return Array.isArray(expressionTarget)
        ? expressionTarget[0]
        : expressionTarget;
    }

    // Try as space item
    try {
      const spaceItem = this.routineContext.getSpaceItem(positionListID);

      if (spaceItem && Array.isArray(spaceItem.positions)) {
        return spaceItem.positions[
          positionListIndex % spaceItem.positions.length
        ];
      }
    } catch (e) {
      // Not a space item, fall through
    }

    // Try as waypoint step (recursively search the tree)
    const rootSteps = this.routineContext.loadedRoutineState?.steps || [];
    const step = this.findStepById(rootSteps, positionListID);

    if (step && step.args) {
      const { args } = step;

      // Check the target structure to determine the type
      if (args.target) {
        // Check if target has positionListID (position list or relative waypoint)
        if ('positionListID' in args.target) {
          // Recursively resolve the position list reference
          return this.resolvePositionListReference(
            args.target.positionListID,
            args.target.positionListIndex ?? positionListIndex,
            previousTargetPose,
          );
        }

        // Check if target has expression
        if ('expression' in args.target) {
          const expressionTarget = await this.routineContext.evaluateExpression(
            args.target.expression,
          );

          return Array.isArray(expressionTarget)
            ? expressionTarget[0]
            : expressionTarget;
        }

        // If target has pose or jointAngles, it's a single position
        if ('pose' in args.target || 'jointAngles' in args.target) {
          return args.target;
        }
      }
    }

    throw new Error(
      `Could not resolve positionListID "${positionListID}" as either a space item or a waypoint step`,
    );
  }

  // Helper to evaluate offset expressions and convert to Euler pose
  private async evaluateOffsetExpressions(): Promise<any | null> {
    const { offsetX, offsetY, offsetZ, offsetRx, offsetRy, offsetRz } =
      this.args.relativeConfig;

    // Evaluate all offset expressions
    const [x, y, z, rx, ry, rz] = await Promise.all([
      this.evaluateOffsetExpression(offsetX, true),
      this.evaluateOffsetExpression(offsetY, true),
      this.evaluateOffsetExpression(offsetZ, true),
      this.evaluateOffsetExpression(offsetRx, false),
      this.evaluateOffsetExpression(offsetRy, false),
      this.evaluateOffsetExpression(offsetRz, false),
    ]);

    // Check if any offsets were evaluated
    if (
      x === null &&
      y === null &&
      z === null &&
      rx === null &&
      ry === null &&
      rz === null
    ) {
      return null;
    }

    // Create EulerPose with capitalized property names (use 0 for undefined values)
    return {
      X: x ?? 0,
      Y: y ?? 0,
      Z: z ?? 0,
      Rx: rx ?? 0,
      Ry: ry ?? 0,
      Rz: rz ?? 0,
    };
  }

  // Helper to evaluate a single offset expression
  private async evaluateOffsetExpression(
    expression: any,
    isPositional: boolean,
  ): Promise<number | null> {
    if (!expression) {
      return null;
    }

    try {
      const result = await this.routineContext.evaluateExpression(expression);

      if (typeof result === 'number') {
        // For positional offsets, convert from the specified distance unit to meters
        if (isPositional && this.args.distanceUnit !== 'meter') {
          return this.convertDistanceToMeters(result, this.args.distanceUnit);
        }

        return result;
      }

      // If result is not a number, try to parse it
      const parsed = parseFloat(String(result));

      if (!Number.isNaN(parsed)) {
        if (isPositional && this.args.distanceUnit !== 'meter') {
          return this.convertDistanceToMeters(parsed, this.args.distanceUnit);
        }

        return parsed;
      }

      return null;
    } catch (error) {
      // If evaluation fails, return null (no offset)
      return null;
    }
  }

  // Helper to convert distance from specified unit to meters
  private convertDistanceToMeters(
    value: number,
    unit: 'meter' | 'millimeter' | 'inch',
  ): number {
    switch (unit) {
      case 'millimeter':
        return value / 1000;
      case 'inch':
        return value * 0.0254;
      case 'meter':
      default:
        return value;
    }
  }

  // Helper to apply an offset pose in the specified frame
  private applyOffsetToPose(pose: any, offsetQuatPose: any, frame: string) {
    switch (frame) {
      case 'base': {
        try {
          const { equipmentItems } = this.routineContext.equipment;

          const customBase = equipmentItems.find(
            (item) => item.config.kind === 'CustomBase' && item.isEnabled,
          );

          if (
            customBase &&
            customBase.config &&
            'rotation' in customBase.config
          ) {
            const { roll, pitch, yaw } = customBase.config.rotation;

            const quaternion = EulerAngleToQuaternion({
              x: MathUtils.degToRad(roll),
              y: MathUtils.degToRad(pitch),
              z: MathUtils.degToRad(yaw),
            });

            // When using a custom base, the robot's coordinate system is rotated
            // So we need to transform the offset to the robot's actual coordinate system
            // before applying it to the pose

            // Transform the offset from world coordinates to robot base coordinates
            const offsetInRobotFrame = rotateVectorByQuaternion(
              { x: offsetQuatPose.x, y: offsetQuatPose.y, z: offsetQuatPose.z },
              quaternion,
            );

            // Create a new offset pose in the robot's coordinate system
            const robotFrameOffset = {
              ...offsetQuatPose,
              x: offsetInRobotFrame.x,
              y: offsetInRobotFrame.y,
              z: offsetInRobotFrame.z,
            };

            // Apply the transformed offset to the pose
            return applyRelativeOffset(pose, robotFrameOffset);
          }

          return applyRelativeOffset(pose, offsetQuatPose);
        } catch (error) {
          this.routineContext.fail({
            failure: { kind: FailureKind.InvalidRoutineLoadedFailure },
            failureReason: 'Invalid custom base configuration',
            error,
          });

          return applyRelativeOffset(pose, offsetQuatPose);
        }
      }
      case 'tool':
        return applyRelativeOffset(
          pose,
          tooltipCoordinatesToBaseCoordinates(offsetQuatPose),
        );
      case 'world': {
        return applyRelativeOffset(pose, offsetQuatPose);
      }
      default: {
        // Try to find a plane space item with this frame ID
        try {
          const spaceItem = this.routineContext.getSpaceItem(frame);

          if (spaceItem.kind === 'plane' && spaceItem.positions.length >= 3) {
            // Create a plane from the space item positions
            const plane = {
              origin: new Vector3(
                spaceItem.positions[0].pose.x,
                spaceItem.positions[0].pose.y,
                spaceItem.positions[0].pose.z,
              ),
              plusX: new Vector3(
                spaceItem.positions[1].pose.x,
                spaceItem.positions[1].pose.y,
                spaceItem.positions[1].pose.z,
              ),
              plusY: new Vector3(
                spaceItem.positions[2].pose.x,
                spaceItem.positions[2].pose.y,
                spaceItem.positions[2].pose.z,
              ),
            };

            // Get the plane's orientation quaternion
            const planeOrientation = getPlaneOrientation(plane);
            const planeToBase = planeOrientation.clone().invert();

            // Transform the orientation of the offset to the plane's coordinate system
            const offsetQ = new ThreeQuaternion(
              offsetQuatPose.i,
              offsetQuatPose.j,
              offsetQuatPose.k,
              offsetQuatPose.w,
            );

            offsetQ.premultiply(planeToBase);

            // Copy the transformed orientation back to the offset pose
            const offsetInPlane = {
              ...offsetQuatPose,
              i: offsetQ.x,
              j: offsetQ.y,
              k: offsetQ.z,
              w: offsetQ.w,
            };

            return applyRelativeOffset(pose, offsetInPlane);
          }
        } catch (e) {
          // Plane not found, fall through to default behavior
        }

        // Default behavior if frame is not recognized or plane not found
        return applyRelativeOffset(pose, offsetQuatPose);
      }
    }
  }

  public async getArmTarget({
    effectiveTCPOption = undefined,
    parentCompletedCount,
    previousTargetPose,
  }: {
    effectiveTCPOption?: TCPOffsetOption;
    parentCompletedCount: number;
    previousTargetPose: CartesianPose;
  }): Promise<
    { target: ArmTarget; poseForPreviousTarget: CartesianPose } | StepFailure
  > {
    const parseTarget = (
      armPosition: any,
    ): { target: ArmTarget; pose: CartesianPose } => {
      const target = {
        motionKind: this.args.motionKind,
        ...armPosition,
        calibrationPose: armPosition.pose,
        // Parent blend config is applied in MoveArmToV2/Step.ts
        blendRadius: this.args.blendConfig.radius,
        stopHere: this.args.stopHere,
      };

      // Create a copy of the pose to avoid reference issues
      const pose = { ...armPosition.pose };

      if (this.args.shouldMatchJointAngles) {
        // If shouldMatchJointAngles is true, we only use joint angles
        target.pose = undefined;

        if (!getFeatureFlagSync('localAccuracyCalibration', false)) {
          target.calibrationPose = undefined;
        }
      } else {
        // remove jointAngle fields
        target.jointAngles = undefined;
        target.calibrationPose = undefined;

        // set effectiveTCPOption
        if (effectiveTCPOption) {
          // weld waypoints use the effectiveTCPOption from the parent weld step
          target.effectiveTCPOption = effectiveTCPOption;
        } else if (this.args.tcpOption === 'auto') {
          if ('tcpOption' in target && target.tcpOption) {
            // use tcpOption that target was originally created with
            // we can leave target.tcpOption as is.
            target.effectiveTCPOption = target.tcpOption;
          } else {
            // use default tcpOption
            target.effectiveTCPOption = DEFAULT_TCP_OFFSET_OPTION;
          }
        } else {
          // use tcpOption that was specified in the Waypoint step
          target.effectiveTCPOption = this.args.tcpOption;
        }
      }

      const parsedTarget = ArmTarget.safeParse(target);

      if (!parsedTarget.success) {
        throw new Error('Move arm target is not valid');
      }

      return { target: parsedTarget.data, pose };
    };

    let target: ArmTarget;
    let anchor: any; // Space Item
    let poseForPreviousTarget: CartesianPose;

    try {
      if ('positionListID' in this.args.target) {
        const { positionListID, positionListIndex } = this.args.target;
        const index = positionListIndex ?? parentCompletedCount;

        const armPosition = await this.resolvePositionListReference(
          positionListID,
          index,
          previousTargetPose,
        );

        const parseResult = parseTarget(armPosition);
        target = parseResult.target;
        poseForPreviousTarget = parseResult.pose;

        // Anchor logic (optional, only for space items)
        try {
          const targetSpace = this.routineContext.getSpaceItem(positionListID);

          if (targetSpace.anchoredToID) {
            anchor = this.routineContext.getSpaceItem(targetSpace.anchoredToID);
          }
        } catch (e) {
          // Not a space item, skip anchor
        }
      } else if ('expression' in this.args.target) {
        const expressionTarget = await this.routineContext.evaluateExpression(
          this.args.target.expression,
        );

        // if expression evaluates to an array, use the first item
        // this makes it easier to use 'SinglePosition' space objects.
        const armPosition = Array.isArray(expressionTarget)
          ? expressionTarget[0]
          : expressionTarget;

        const parseResult = parseTarget(armPosition);
        target = parseResult.target;
        poseForPreviousTarget = parseResult.pose;
      } else {
        const parseResult = parseTarget(this.args.target);
        target = parseResult.target;
        poseForPreviousTarget = parseResult.pose;
      }

      // Check if any offset expressions are present
      const hasOffsets =
        this.args.relativeConfig.offsetX ||
        this.args.relativeConfig.offsetY ||
        this.args.relativeConfig.offsetZ ||
        this.args.relativeConfig.offsetRx ||
        this.args.relativeConfig.offsetRy ||
        this.args.relativeConfig.offsetRz;

      if ('pose' in target && hasOffsets && !this.args.shouldMatchJointAngles) {
        // Application of relative configuration offset
        const offsetValues = await this.evaluateOffsetExpressions();

        if (offsetValues) {
          const offsetQuatPose = convertEulerPose.toCartesian(offsetValues);

          // Apply offset to pose for cartesian waypoints
          const offsetResult = this.applyOffsetToPose(
            target.pose,
            offsetQuatPose,
            this.args.relativeConfig.frame,
          );

          if (
            offsetResult &&
            typeof offsetResult === 'object' &&
            'failure' in offsetResult
          ) {
            return {
              failure: {
                kind: FailureKind.InvalidRoutineLoadedFailure,
              },
              failureReason: (offsetResult as any).failureReason,
              error: (offsetResult as any).error,
            };
          }

          target.pose = offsetResult;
        }
      }

      if (
        'positionListID' in this.args.target &&
        anchor &&
        anchor.kind === 'localAccuracyCalibration'
      ) {
        if ('pose' in target) {
          target.pose = this.routineContext.getCalibratedGoal(
            target.pose,
            anchor,
          );
        } else if ('jointAngles' in target && 'calibrationPose' in target) {
          const calibratedPose = this.routineContext.getCalibratedGoal(
            target.calibrationPose,
            anchor,
          );

          // do not change the original target object if pose did not change
          if (!posesAreEqual(target.calibrationPose, calibratedPose)) {
            const newJointAngles =
              await this.routineContext.getJointAnglesForCartesianSpacePose(
                calibratedPose,
                this.args.motionKind,
                false,
              );

            if (newJointAngles) {
              target.jointAngles = newJointAngles;
            } else {
              throw new Error(
                'Offset pose is unreachable: could not solve for joint angles',
              );
            }
          }
        }
      }

      if ('pose' in target) {
        poseForPreviousTarget = target.pose;
      }

      return { target, poseForPreviousTarget };
    } catch (error) {
      return {
        failure: {
          kind: FailureKind.InvalidRoutineLoadedFailure,
        },
        failureReason: error.message,
        error,
      };
    }
  }
}
